import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface AuthUser {
    id: number;
    name: string;
    email: string;
}

interface AuthPageProps {
    onAuthenticated: (token: string, user: AuthUser) => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';

export function AuthPage({ onAuthenticated }: AuthPageProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const initialMode = useMemo(() => {
        return new URLSearchParams(location.search).get('mode') === 'signup' ? 'signup' : 'login';
    }, [location.search]);
    const [mode, setMode] = useState<'login' | 'signup'>(initialMode);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const endpoint = mode === 'signup' ? '/api/auth/register' : '/api/auth/login';
            const payload =
                mode === 'signup'
                    ? { name, email, password }
                    : { email, password };

            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data?.message || 'Authentication failed');
                return;
            }

            onAuthenticated(data.token, data.user as AuthUser);
        } catch {
            setError(t('auth.serverError'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 pt-20">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-900">
                        {mode === 'signup' ? t('auth.signupTitle') : t('auth.loginTitle')}
                    </h1>
                    <p className="text-slate-500 mt-2">
                        {mode === 'signup' ? t('auth.signupSubtitle') : t('auth.loginSubtitle')}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'signup' && (
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.name')}</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                required
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">{t('auth.password')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            minLength={6}
                            required
                        />
                    </div>

                    {error && (
                        <p className="text-sm text-red-600">{error}</p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-500 disabled:opacity-60"
                    >
                        {loading ? t('auth.loading') : mode === 'signup' ? t('auth.submitSignup') : t('auth.submitLogin')}
                    </button>
                </form>

                <div className="mt-5 text-sm text-slate-600">
                    {mode === 'signup' ? t('auth.alreadyAccount') + ' ' : t('auth.noAccount') + ' '}
                    <button
                        type="button"
                        onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}
                        className="text-indigo-600 hover:text-indigo-500 font-semibold"
                    >
                        {mode === 'signup' ? t('auth.switchToLogin') : t('auth.switchToSignup')}
                    </button>
                </div>

                <Link to="/" className="inline-block mt-5 text-sm text-slate-500 hover:text-slate-700">
                    {t('auth.backToHome')}
                </Link>
            </div>
        </div>
    );
}
